import hashObject from 'object-hash';

export type IdGenerator<T extends object = object> = (
    input: { props: T; id?: undefined } | { id: string; props?: undefined }
) => string;

/**
 * Create a unique predictable ID for an input object.
 * The ID is generated by hashing the properties of the object and appending an incrementing number.
 * This ensures that the ID is unique even if the properties are the same.
 * The ID is a base36 string with a maximum length of 40 characters.
 *
 * As the generator is sequential, it is important to use it in a predictable order.
 */
export function createPredictableIdGenerator<T extends object>({
    seed,
}: {
    /** Seed to avoid conflicts with other predictable IDs */
    seed: string;
}): IdGenerator<T> {
    const originalIdToResult = new Map<string, string>();
    const hashToIndex = new Map<string, number>();

    return (input: { props: T; id?: undefined } | { id: string; props?: undefined }): string => {
        if (input.id) {
            const existing = originalIdToResult.get(input.id);
            if (existing) {
                return existing;
            }
        }

        const common = input.props ? { seed, props: input.props } : { seed, id: input.id };

        const baseHash = hashObject(common);
        let index = hashToIndex.get(baseHash) ?? 0;

        index += 1;
        hashToIndex.set(baseHash, index);

        if (index === 1) {
            if (input.id) {
                originalIdToResult.set(input.id, baseHash);
            }
            return baseHash;
        }

        const hash = hashObject({ ...common, index });
        if (input.id) {
            originalIdToResult.set(input.id, hash);
        }
        return hash;
    };
}

export function createPredictableSeed(input: object) {
    return hashObject(input);
}
